import { useContext, useEffect, useState } from 'react';
import { Button, Table, Spinner, Alert, Nav } from 'react-bootstrap';
import { FaEye, FaShippingFast, FaEraser } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import SuccessModal from '../components/modals/SuccessModal';
import { useNotificationModal } from '../hooks/useNotificationModal';
import '../styles/WarehouseIssuing.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function WarehouseIssuing() {
  const { userData } = useContext(AuthContext);
  const navigate = useNavigate();
  const company_code = userData?.company_code;
  
  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showError,
    hideSuccess,
    hideError
  } = useNotificationModal();

  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  // Column-level filters for orders table
  const [orderColumnFilter, setOrderColumnFilter] = useState({
    index: '',
    order_number: '',
    customer_name: '',
    total_amount: '',
    total_items: '',
    date: '',
    issuing_id: '',
    issued_by: ''
  });

  // Helper: case-insensitive substring match
  const matches = (value, filter) => {
    if (!filter) return true;
    const val = (value === null || value === undefined) ? '' : String(value);
    return val.toLowerCase().includes(filter.toLowerCase());
  };

   // Clear all filters
    const clearFilters = () => {
      setOrderColumnFilter({
        index: '',
        order_number: '',
        customer_name: '',
        total_amount: '',
        total_items: '',
        date: '',
        issuing_id: '',
        issued_by: ''
      });
    };

  // Fetch orders
  useEffect(() => {
    if (!company_code) return;
    setLoading(true);
    axios.get(`${BASE_URL}/api/admin/issuing/orders`, {
      params: { company_code }
    })
      .then(response => {
        const ordersData = Array.isArray(response.data) ? response.data : [];
        setOrders(ordersData);
        setLoading(false);
      })
      .catch(err => {
        showError(err.response?.data?.message || 'Failed to load orders', 'Error');
        setError('Failed to load orders');
        setLoading(false);
      });
  }, [company_code, showError]);

  // Filter orders based on active tab
  useEffect(() => {
    if (activeTab === 'pending') {
      setFilteredOrders(orders.filter(order => 
        order.order_status === 'Pending' 
      ));
    } else if (activeTab === 'in-transit') {
      setFilteredOrders(orders.filter(order => 
        order.order_status === 'In Transit' 
      ));
    }
  }, [orders, activeTab]);

  // Navigate to order details
  const handleViewOrderDetails = (order) => {
    navigate(`/order-details/${order.order_id}`);
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  return (
    <div className="warehouse-issuing-page">
      <div className="wh-issuing-header">
        <h1>Warehouse Issuing - Orders</h1>
      </div>
      
      {/* Filter Tabs */}
      <Nav variant="tabs" className="mb-3">
        <Nav.Item>
          <Nav.Link 
            active={activeTab === 'pending'} 
            onClick={() => handleTabChange('pending')}
            style={{ cursor: 'pointer' }}
          >
            Pending
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            active={activeTab === 'in-transit'} 
            onClick={() => handleTabChange('in-transit')}
            style={{ cursor: 'pointer' }}
          >
            In Transit
          </Nav.Link>
        </Nav.Item>
      </Nav>
      
      {loading ? <Spinner animation="border" /> : error ? <Alert variant="danger">{error}</Alert> : (
        <Table striped bordered hover size="sm" className='issue-table'>
          <thead>
            <tr>
              <th>#</th>
              <th>Order Number</th>
              <th>Customer Name</th>
              <th>Total Amount</th>
              <th>Total Items</th>
              {/* Only show Issuing ID and Issued By for in-transit tab */}
              {activeTab === 'in-transit' && <th>Issuing ID</th>}
              {activeTab === 'in-transit' && <th>Issued By</th>}
              <th>{activeTab === 'pending' ? 'Created At' : 'Issued At'}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* Column-level search inputs - first tbody row */}
            <tr className="table-filters-row">
              <td>
              </td>
              <td>
                <input
                  className="form-control form-control-sm wi-search"
                  type="text"
                  placeholder="Order Number"
                  value={orderColumnFilter.order_number}
                  onChange={e => setOrderColumnFilter({ ...orderColumnFilter, order_number: e.target.value })}
                />
              </td>
              <td>
                <input
                  className="form-control form-control-sm wi-search"
                  type="text"
                  placeholder="Customer"
                  value={orderColumnFilter.customer_name}
                  onChange={e => setOrderColumnFilter({ ...orderColumnFilter, customer_name: e.target.value })}
                />
              </td>
              <td>
                <input
                  className="form-control form-control-sm wi-search"
                  type="text"
                  placeholder="Amount"
                  value={orderColumnFilter.total_amount}
                  onChange={e => setOrderColumnFilter({ ...orderColumnFilter, total_amount: e.target.value })}
                />
              </td>
              <td>
                <input
                  className="form-control form-control-sm wi-search"
                  type="text"
                  placeholder="Items"
                  value={orderColumnFilter.total_items}
                  onChange={e => setOrderColumnFilter({ ...orderColumnFilter, total_items: e.target.value })}
                />
              </td>
              {/* Issuing ID and Issued By filters */}
              {activeTab === 'in-transit' && (
                <>
                  <td>
                    <input
                      className="form-control form-control-sm wi-search"
                      type="text"
                      placeholder="Issuing ID"
                      value={orderColumnFilter.issuing_id}
                      onChange={e => setOrderColumnFilter({ ...orderColumnFilter, issuing_id: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm wi-search"
                      type="text"
                      placeholder="Issued By"
                      value={orderColumnFilter.issued_by}
                      onChange={e => setOrderColumnFilter({ ...orderColumnFilter, issued_by: e.target.value })}
                    />
                  </td>
                </>
              )}
              <td>
                <input
                  className="form-control form-control-sm wi-search"
                  type="date"
                  placeholder="Date"
                  value={orderColumnFilter.date}
                  onChange={e => setOrderColumnFilter({ ...orderColumnFilter, date: e.target.value })}
                />
              </td>
              <td style={{ whiteSpace: 'nowrap', width: '1%' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    variant="outline"
                    className="pf-clear-filters-btn"
                    onClick={(e) => { e.stopPropagation(); clearFilters(); }}
                    title="Clear filters"
                  >
                    <FaEraser />
                  </Button>
                </div>
              </td>
            </tr>

            {(() => {
              const displayed = filteredOrders.filter((order, idx) => {
                if (!matches(String(idx + 1), orderColumnFilter.index)) return false;
                if (!matches(order.order_number, orderColumnFilter.order_number)) return false;
                if (!matches(order.customer_name, orderColumnFilter.customer_name)) return false;
                if (!matches(parseFloat(order.total_amount || 0).toFixed(2), orderColumnFilter.total_amount)) return false;
                if (!matches(String(order.total_items || ''), orderColumnFilter.total_items)) return false;
                const getOrderDateISO = (o) => {
                  const d = activeTab === 'pending' ? o.created_at : (o.issued_at || o.created_at);
                  if (!d) return '';
                  try {
                    const dt = new Date(d);
                    if (isNaN(dt.getTime())) return '';
                    const yyyy = dt.getFullYear();
                    const mm = String(dt.getMonth() + 1).padStart(2, '0');
                    const dd = String(dt.getDate()).padStart(2, '0');
                    return `${yyyy}-${mm}-${dd}`;
                  } catch (e) {
                    return '';
                  }
                };
                const orderDateISO = getOrderDateISO(order);
                if (orderColumnFilter.date && orderColumnFilter.date !== '') {
                  if (orderDateISO !== orderColumnFilter.date) return false;
                }
                  // Issuing ID and Issued By filters 
                  if (!matches(order.issuing_id || '', orderColumnFilter.issuing_id)) return false;
                  // Use issuer_email if available, otherwise fall back to issued_by
                  const issuerDisplay = order.issuer_email || order.issued_by || '';
                  if (!matches(issuerDisplay, orderColumnFilter.issued_by)) return false;
                return true;
              });

              if (displayed.length === 0) {
                // Compute proper colspan depending on whether show the extra columns
                const colSpan = activeTab === 'in-transit' ? 9 : 7;
                return (
                  <tr>
                    <td colSpan={colSpan} className="text-center">No {activeTab === 'pending' ? 'pending' : 'in-transit'} orders found.</td>
                  </tr>
                );
              }

              return displayed.map((order, idx) => (
                <tr key={order.order_id}>
                  <td>{idx + 1}</td>
                  <td>{order.order_number}</td>
                  <td>{order.customer_name}</td>
                  <td className="numbers-col">
                    ${parseFloat(order.total_amount || 0).toFixed(2)}
                  </td>
                  <td className="numbers-col">{order.total_items}</td>
                  {activeTab === "in-transit" && (
                    <>
                      <td>{order.issuing_id || "N/A"}</td>
                      <td>{order.issuer_email || order.issued_by || "N/A"}</td>
                    </>
                  )}
                  <td>
                    {activeTab === "pending"
                      ? new Date(order.created_at).toLocaleDateString()
                      : order.issued_at
                      ? new Date(order.issued_at).toLocaleDateString()
                      : "N/A"}
                  </td>
                  <td>
                    {activeTab === "pending" ? (
                      <FaShippingFast
                        size="20px"
                        onClick={() => handleViewOrderDetails(order)}
                        title="Issue Order Items"
                        className="FaShippingFast"
                      />
                       
                    ) : (
                      <FaEye
                        size="20px"
                        onClick={() => handleViewOrderDetails(order)}
                        title="View Order Details"
                        className="FaEye"
                      />
                    )}
                  </td>
                </tr>
              ));
            })()}
          </tbody>
        </Table>
      )}
      
      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        variant="success"
        modalTitle={successTitle}
        message={successMessage}
      />
      
      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        variant="error"
        modalTitle={errorTitle}
        message={errorMessage}
      />
    </div>
  );
}
